import _ from "lodash";

const INITIAL_RECONNECT_DELAY_IN_MS = 1000;
const MAX_RECONNECT_DELAY_IN_MS = 30 * 1000;
const INVALID_PAYLOAD_CLOSE_STATUS = 1007;
const POLICY_VIOLATION_CLOSE_STATUS = 1008;

type ClientNotificationHandler = (clientNotification: ClientNotification) => void;

interface ClientNotification {
    event: string;
    target?: string;
    data?: any;
}

interface ClientNotificationSubscription {
    event: string;
    target?: string;
    handler: ClientNotificationHandler;
}

export class ClientNotificationService {

    private webSocket?: WebSocket;

    private subscriptions: ClientNotificationSubscription[] = [];

    private publicAccessUserId: string | null = null;

    private reconnectDelayInMs = INITIAL_RECONNECT_DELAY_IN_MS;

    private reconnectTimer?: ReturnType<typeof setTimeout>;

    private disconnectRequested = false;

    public connectToServer(publicAccessUserId?: string | null) {
        if (this.webSocket?.readyState === WebSocket.CONNECTING || this.webSocket?.readyState === WebSocket.OPEN) {
            return;
        }

        this.disconnectRequested = false;
        this.publicAccessUserId = publicAccessUserId ?? null;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);

            this.reconnectTimer = undefined;
        }

        this.openConnection();
    }

    private openConnection() {
        const protocol = (window.location.protocol === "https:") ? "wss://" : "ws://";

        const publicAccessQuery = this.publicAccessUserId
            ? `?publicAccessUserId=${encodeURIComponent(this.publicAccessUserId)}`
            : "";

        const webSocket = new WebSocket(`${protocol}${window.location.host}/api/notifications${publicAccessQuery}`);

        this.webSocket = webSocket;

        webSocket.onerror = ev => {
            console.debug("onerror: ", ev);
        }

        webSocket.onopen = ev => {
            console.debug("onopen: ", ev);

            this.reconnectDelayInMs = INITIAL_RECONNECT_DELAY_IN_MS;

            for (const subscription of this.subscriptions) {
                webSocket.send(JSON.stringify({
                    action: "subscribe",
                    event: subscription.event,
                    target: subscription.target
                }));
            }
        }

        webSocket.onclose = ev => {
            console.debug("onclose: socket closed", ev);

            if (this.webSocket === webSocket) {
                this.webSocket = undefined;
            }

            if (this.disconnectRequested
                || ev.code === INVALID_PAYLOAD_CLOSE_STATUS
                || ev.code === POLICY_VIOLATION_CLOSE_STATUS) {
                return;
            }

            const reconnectDelayInMs = this.reconnectDelayInMs;

            this.reconnectDelayInMs = Math.min(this.reconnectDelayInMs * 2, MAX_RECONNECT_DELAY_IN_MS);

            this.reconnectTimer = setTimeout(() => {
                this.reconnectTimer = undefined;

                if (!this.disconnectRequested) {
                    this.openConnection();
                }
            }, reconnectDelayInMs);
        }

        webSocket.onmessage = message => {
            console.debug("onmessage: ", message);

            const notification = JSON.parse(message.data);

            const event = notification.event;
            const target = notification.target;
            const data = notification.data;

            for (const subscription of this.subscriptions) {
                if (subscription.event === event && subscription.target === target) {
                    subscription.handler({event, target, data});
                }
            }
        }
    }

    public disconnectFromServer() {
        this.disconnectRequested = true;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);

            this.reconnectTimer = undefined;
        }

        const webSocket = this.webSocket;

        this.webSocket = undefined;

        if (webSocket) {
            webSocket.onclose = null;
            webSocket.close();
        }
    }

    public subscribe(event: string, handler: ClientNotificationHandler, target?: string) {
        this.subscriptions.push({event, target, handler});

        if (this.webSocket?.readyState === WebSocket.CONNECTING) {
            console.debug("WebSocket is in CONNECTING state, subscription will be sent after connecting");

            return;
        }

        if (this.webSocket?.readyState !== WebSocket.OPEN) {
            console.debug("WebSocket is not in OPEN state, subscription will be sent after reconnecting");

            return;
        }

        this.webSocket?.send(JSON.stringify({action: "subscribe", event, target}));

        console.debug("Registered subscription for " + event + " event (target: " + target + ")");
    }

    public unsubscribe(event: string, target?: string) {
        if (this.webSocket?.readyState === WebSocket.OPEN) {
            this.webSocket?.send(JSON.stringify({action: "unsubscribe", event, target}));
        }

        _.remove(this.subscriptions, (subscription: ClientNotificationSubscription) => subscription.event === event && subscription.target === target);

        console.debug("Removed notification subscription for " + event + " event (target: " + target + ")");
    }
}
